import type { Project } from "@tudigo-monorepo/core-tudigo-api-models";
import { RestrictedAccess } from "@tudigo-monorepo/tudigo-session";
import {
  ComparatorGrid,
  ComparatorGridColumn,
  ComparatorModal,
  ComparatorModalHeader,
  ComparatorModalTitle,
  ComparatorScroller,
  ComparatorSelectedItem,
  ComparatorSelectionList,
  ComparatorShareDialog,
  useComparator,
} from "@tudigo-monorepo/web-tudigo-comparator";
import { DialogSeparator } from "@tudigo-monorepo/web-tudigo-components";
import {
  UI_PROJECTS_COMPARATOR_MODAL_CLOSED,
  UI_PROJECTS_COMPARATOR_MODAL_PROJECT_DELETED,
  UI_PROJECTS_COMPARATOR_SHARE_CLICKED,
  UI_PROJECTS_COMPARATOR_SHARE_COPY_CLICKED,
} from "@tudigo-monorepo/web-tudigo-tracking";

import { AppointmentCardNew } from "../../../../../shared-external/components/appointment-card-new";
import { useProjectsComparatorCustomerTracking } from "../../../hooks/use-projects-comparator-customer-tracking";
import { ProjectsComparatorLegalBlock } from "./projects-comparator-legal-block";
import { ProjectsComparatorProjectCard } from "./projects-comparator-project-card";
import { ProjectsComparatorProjectLink } from "./projects-comparator-project-link";
import { ProjectsComparatorSimulatorAmount } from "./projects-comparator-simulator/projects-comparator-simulator-amount";
import { ProjectsComparatorSimulatorRoiCard } from "./projects-comparator-simulator/projects-comparator-simulator-roi-card";
import { ProjectsComparatorStickySelectionList } from "./projects-comparator-sticky-selection-list";
import { ProjectsComparatorSuggestionBlock } from "./projects-comparator-suggestion-block";
import { ProjectsComparatorActivityTable } from "./projects-comparator-tables/projects-comparator-activity-table";
import { ProjectsComparatorAdvancedInformationsTable } from "./projects-comparator-tables/projects-comparator-advanced-informations-table";
import { ProjectsComparatorDealTable } from "./projects-comparator-tables/projects-comparator-deal-table";
import { ProjectsComparatorGeneralInformationsTable } from "./projects-comparator-tables/projects-comparator-general-informations-table";

export function ProjectsComparatorModal() {
  const { urlState, resetComparator } = useComparator<Project>();
  const { comparatorStatePayload, customerTrackComparator } =
    useProjectsComparatorCustomerTracking();

  return (
    <RestrictedAccess
      openAsDialog
      defaultOpen={urlState.mode !== null}
      onClose={resetComparator}
    >
      <ComparatorModal
        onUpdate={(open) => {
          if (!open) {
            customerTrackComparator(
              UI_PROJECTS_COMPARATOR_MODAL_CLOSED,
              comparatorStatePayload,
            );
          }
        }}
      >
        <ComparatorModalHeader className="py-6 sm:py-8">
          <ComparatorModalTitle>
            Comparaison d&apos;opportunités
          </ComparatorModalTitle>
          <ComparatorShareDialog
            onUpdate={(value) => {
              if (typeof value === "boolean" && value) {
                customerTrackComparator(
                  UI_PROJECTS_COMPARATOR_SHARE_CLICKED,
                  comparatorStatePayload,
                );
              }
              if (typeof value === "string" && value === "copy") {
                customerTrackComparator(
                  UI_PROJECTS_COMPARATOR_SHARE_COPY_CLICKED,
                  comparatorStatePayload,
                );
              }
            }}
          />
        </ComparatorModalHeader>

        <DialogSeparator className="mb-6" />

        <ProjectsComparatorSimulatorAmount />

        <ComparatorScroller
          scrollableItemWidth={338}
          bottomScrollButtons
          stickybar={{
            children: <ProjectsComparatorStickySelectionList />,
          }}
        >
          <ComparatorGrid className="mb-6 lg:mt-6">
            <ComparatorSelectionList>
              {(project: Project) => (
                <ComparatorGridColumn key={project.id}>
                  <ComparatorSelectedItem
                    item={project}
                    removeButtonAlignOffset="top"
                    className="h-full min-w-[338px]"
                    onUpdate={() => {
                      customerTrackComparator(
                        UI_PROJECTS_COMPARATOR_MODAL_PROJECT_DELETED,
                        {
                          project_id: project.id,
                          project_name: project.content?.name,
                        },
                      );
                    }}
                  >
                    <ProjectsComparatorProjectCard project={project} />
                    {urlState.amount !== null && (
                      <ProjectsComparatorSimulatorRoiCard
                        amountInCents={urlState.amount * 100}
                        project={project}
                      />
                    )}
                    <ProjectsComparatorProjectLink project={project} />
                  </ComparatorSelectedItem>
                </ComparatorGridColumn>
              )}
            </ComparatorSelectionList>
          </ComparatorGrid>

          <ProjectsComparatorGeneralInformationsTable />
          <ProjectsComparatorDealTable />
          <ProjectsComparatorActivityTable />
          <ProjectsComparatorAdvancedInformationsTable />

          <ComparatorGrid className="my-6">
            <ComparatorSelectionList>
              {(project: Project) => (
                <ComparatorGridColumn
                  key={project.id}
                  className="min-w-[338px]"
                >
                  <ProjectsComparatorProjectLink project={project} />
                </ComparatorGridColumn>
              )}
            </ComparatorSelectionList>
          </ComparatorGrid>
        </ComparatorScroller>

        <div className="mt-8 border-t border-neutral-200 bg-neutral-50 p-4 lg:px-16 lg:py-8">
          <AppointmentCardNew />
        </div>

        <ProjectsComparatorSuggestionBlock />
        <ProjectsComparatorLegalBlock />
      </ComparatorModal>
    </RestrictedAccess>
  );
}
